import React from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import ResumeSection from '../components/ResumeSection';
import SingleSectionView from '../com/SingleSectionView';
import { baseInfoSection } from '../configs/sectionConfig';
import { buildBaseInfoParams, getTreePath } from '../utils/resumeTransforms';
import Config from '../../../config/Config';
import WidgetAjax from '../../../widget/WidgetAjax';

/**
 * 基本信息 模块 (class 组件版)
 * - 编辑态由 this.state.editing 自管理,formRef 通过 React.createRef 持有
 * - Props: info / options / applicantId / onChanged
 * - 字段定义复用 baseInfoSection,展示/编辑容器复用 ResumeSection + SingleSectionView
 * - 保存直接在本模块调 /applicant/update,参数走 buildBaseInfoParams
 *   - status === 0: 提示成功 → 关闭编辑 → 触发 onChanged
 *   - 失败: 提示后端 message → 保留编辑态
 * - this.initialValues 是**一次编辑会话的快照**(class 字段,不放 state):
 *   - handleEdit 时一次性快照 this.buildInitialValues(),
 *     后续 render 直接复用同一个对象引用,避免 AntD Form 在父组件重渲染
 *     时收到新引用而把已经填好的字段重置回初值。
 *   - handleCancel / 保存成功 → 清空快照
 *   - this.state 仅包含 editing,符合"state 只管理 editing"的要求。
 * - 日期/地区初始值转换与原 BaseInfoSection 保持一致
 */
class BasicInfoModule extends React.Component {
  formRef = React.createRef();

  /**
   * 当前编辑会话的初始值快照;非 state。
   * 编辑期间该引用保持稳定,父组件重渲染也不会让 AntD Form 重新应用 initialValues。
   */
  initialValues = null;

  constructor(props) {
    super(props);
    this.state = {
      editing: false,
    };
  }

  handleEdit = () => {
    // 一次性快照:进入编辑态时锁定初始值,避免后续 props 更新覆盖用户输入
    this.initialValues = this.buildInitialValues();
    this.setState({ editing: true });
  };

  handleCancel = () => {
    this.initialValues = null;
    this.setState({ editing: false });
  };

  /**
   * 表单提交: 组装后端入参并直接发请求。
   * - 成功 (res.status === 0): 关闭编辑,通知父组件刷新
   * - 失败: 弹后端 message,保留编辑态以便用户继续修改
   */
  handleSubmit = (values) => {
    const { info, applicantId, onChanged } = this.props;
    const params = buildBaseInfoParams(info, values, applicantId);

    WidgetAjax.ajax({
      url: `${Config.getBasePath()}/applicant/update`,
      params,
      callback: (res) => {
        if (res && res.status === 0) {
          message.success('保存成功');
          this.initialValues = null;
          this.setState({ editing: false });
          if (typeof onChanged === 'function') {
            onChanged();
          }
          return;
        }
        message.error((res && res.message) || '保存失败');
      },
    });
  };

  /**
   * 编辑态初始值:
   * - BRTHDATE / WKDATE → dayjs 实例,DatePicker 才能回填
   * - REGDISTRICT / RESDISTRICT → 区域树路径,Cascader 才能回填
   * - 其它字段沿用 info 原值(姓名/性别/邮箱等)
   *
   * 仅在 handleEdit 时调用一次,结果保存到 this.initialValues 快照字段。
   */
  buildInitialValues = () => {
    const { info, options } = this.props;
    if (!info) return undefined;
    return {
      ...info,
      BRTHDATE: info.BRTHDATE ? dayjs(info.BRTHDATE) : undefined,
      WKDATE: info.WKDATE ? dayjs(info.WKDATE) : undefined,
      REGDISTRICT: getTreePath(info.REGDISTRICT, options && options.areaList),
      RESDISTRICT: getTreePath(info.RESDISTRICT, options && options.areaList),
    };
  };

  render() {
    const { info, options, applicantId } = this.props;
    const { editing } = this.state;

    return (
      <ResumeSection
        section={baseInfoSection}
        editing={editing}
        onEdit={this.handleEdit}
      >
        <SingleSectionView
          section={baseInfoSection}
          data={info}
          editing={editing}
          options={options}
          applicantId={applicantId}
          formRef={this.formRef}
          onSubmit={this.handleSubmit}
          onCancel={this.handleCancel}
          initialValues={editing ? this.initialValues : undefined}
        />
      </ResumeSection>
    );
  }
}

export default BasicInfoModule;